import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COMPETENCIES = [
  { id: "quality", label: "Quality of work" },
  { id: "impact", label: "Impact & outcomes" },
  { id: "collaboration", label: "Collaboration" },
  { id: "ownership", label: "Ownership" },
  { id: "growth", label: "Growth mindset" },
];

const CURRENT_CYCLE = "H1_2026";

export default function MeReviews() {
  const [past, setPast] = useState([]);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState("");

  const load = () => api.get("/hr/reviews/mine").then(r=>setPast(r.data)).catch(()=>setPast([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (Object.keys(ratings).length < COMPETENCIES.length) return toast.error("Rate every competency 1-5");
    await api.post("/hr/reviews/self", { cycle: CURRENT_CYCLE, ratings, comments });
    toast.success("Self-review saved");
    load();
  };

  return (
    <div className="space-y-4" data-testid="me-reviews">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Performance</div>
        <h1 className="text-3xl font-bold font-display mt-1">Self review · {CURRENT_CYCLE}</h1>
        <p className="text-sm text-neutral-500 mt-1">Rate yourself on each competency 1-5. Your manager reviews next.</p>
      </div>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-4">
          {COMPETENCIES.map(c => (
            <div key={c.id} data-testid={`review-${c.id}`}>
              <div className="flex justify-between text-sm mb-2"><span className="font-medium">{c.label}</span><span className="text-neutral-500 font-mono">{ratings[c.id] || "—"}/5</span></div>
              <div className="flex gap-2">
                {[1,2,3,4,5].map(n => (
                  <button
                    key={n}
                    onClick={()=>setRatings({...ratings, [c.id]: n})}
                    data-testid={`review-${c.id}-${n}`}
                    className={`w-10 h-10 rounded-lg border transition-all ${ratings[c.id]===n ? "bg-pink text-white border-pink font-bold" : "hover:border-pink"}`}>{n}</button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Label className="text-xs">Reflections (optional)</Label>
            <Textarea rows={4} value={comments} onChange={e=>setComments(e.target.value)} placeholder="What went well? What would you do differently?" data-testid="review-comments" />
          </div>
          <Button onClick={submit} className="btn-primary rounded-full" data-testid="review-submit">Submit self-review</Button>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-3">Past reviews</h3>
          <div className="space-y-2">
            {past.length === 0 && <div className="text-sm text-neutral-500">No prior reviews yet.</div>}
            {past.map(r => (
              <div key={r.review_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div><div className="text-sm font-semibold">Cycle {r.cycle}</div><div className="text-xs text-neutral-500 mt-0.5">{new Date(r.created_at).toLocaleDateString()}</div></div>
                <Badge variant="outline">{r.status}</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
